// Nav v2: the dashboard shell. A slim rail of sections with the workspace and site switcher on top; the section's
// pages as horizontal tabs in a sticky header; a tab's views as a segmented switch. On a phone the rail becomes a
// top bar (switcher, search) and a bottom bar (the sections), and the tabs scroll sideways.
//
// Everything about WHERE comes from one map, lib/navMap.ts: which section and tab the address is, which tabs exist
// for the scope, and where each one goes. This file only draws it.
//
// THE ADDRESS WINS OVER THE SWITCHER. An address that names a site (/sites/35, /analytics?site=…, /tasks?site=…) or
// means All sites (/sites, /pages, /analytics, /submissions) sets the scope; an address that says nothing
// (Connections, More tools) keeps the one chosen. So a shared link opens the scope it was copied in, and the switcher can never
// claim one site while the screen shows another.
//
// THE PROJECT (the switcher's left column, lib/projectScopeContext.tsx) is held here for every screen under the shell.
// `?project=<id>` (or `none`, `all`) in an address sets it, and picking one writes it there, so a link opens the same
// project; otherwise it is remembered like the site. An address that names a website outside the chosen project moves
// the project back to All sites, for the same reason the address wins over the site. The data workspace has its own
// picker in the header of the screens that read it (More tools, Connections, Settings › Workspace).
import { prefetchEditor } from "@/features/site-workspace/prefetch";
import { useEffect, useMemo, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import * as Pop from "@radix-ui/react-popover";
import {
  ChevronsUpDown, CircleUser, CreditCard, Ellipsis, FileText, House, Inbox, LayoutGrid, LogOut, Monitor, Moon, PanelsTopLeft, Plug,
  Search, ShieldCheck, SlidersHorizontal, Sun, ChartNoAxesColumn,
} from "lucide-react";
import { RouteContent } from "@/components/RouteContent";
import { CommandPalette } from "@/components/CommandPalette";
import { FeedbackRecorder } from "@/components/feedback/FeedbackRecorder";
import { ScopeSwitcher, SiteTile } from "./ScopeSwitcher";
import { ScopeSelector } from "@/components/ScopeSelector";
import { useAuth } from "@/lib/auth";
import { useTheme, type Theme } from "@/lib/theme";
import { useScope } from "@/lib/scope";
import { useWebsites, type WebsiteRecord } from "@/lib/websites";
import { ProjectScopeProvider, useProjectScope } from "@/lib/projectScopeContext";
import { PROJECT_PARAM, choiceForAddress, choiceLabel, inProjectFilter, parseProjectChoice, websiteSiteId, withProjectParam, type ProjectChoice } from "@/lib/projectScope";
import { NavChromeContext, useNavBase } from "@/lib/navChrome";
import { SECTIONS, findSite, resolveRoute, scopeTarget, sectionHref, sectionOf, sectionTabs, type NavEnv, type NavSite, type SectionId } from "@/lib/navMap";
import { cn } from "@/lib/utils";
import "./shell.css";

const LOGO = "https://res.cloudinary.com/dqa47jizr/image/upload/q_auto/f_auto/v1777471110/bloxx-logo_gedexe.png";

const ICONS: Record<SectionId, LucideIcon> = {
  home: House, content: FileText, review: ShieldCheck, analytics: ChartNoAxesColumn, leads: Inbox,
  settings: SlidersHorizontal, connections: Plug, more: LayoutGrid,
};
/** The bottom bar on a phone: the five sections used daily, then More. */
const PHONE: SectionId[] = ["home", "content", "review", "analytics", "leads"];

const ENV: NavEnv = {
  scheduling: import.meta.env.VITE_SCHEDULING_ENABLED === "true",
  businesses: import.meta.env.VITE_BUSINESS_IDENTITY === "1",
};

/** Screens whose content IS the page rather than a document on it (as in the classic layout). */
const isFullBleed = (path: string) => path.startsWith("/tables");
/** Screens that are one long list (Content › Pages): the header and the list use the screen's width, not a 72rem column. */
const isWide = (path: string) => path === "/pages";

function openSearch() {
  const mac = typeof navigator !== "undefined" && /mac|iphone|ipad/i.test(navigator.platform || "");
  window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true, ctrlKey: !mac, bubbles: true }));
}

export function Shell() {
  return <ProjectScopeProvider><ShellBody /></ProjectScopeProvider>;
}

/** Screens that read the data workspace (Tables, Interfaces, Studio, Connections' checks): its picker sits in their header. */
const readsDataWorkspace = (at: { section: SectionId; tab: string } | null) => !!at && (at.section === "more" || at.section === "connections" || (at.section === "settings" && at.tab === "workspace"));

function ShellBody() {
  useEffect(() => { performance.mark("bloxx:shell-visible"); }, []);
  const base = useNavBase();
  const location = useLocation();
  const navigate = useNavigate();
  const path = base && location.pathname.startsWith(base) ? location.pathname.slice(base.length) || "/" : location.pathname;
  const at = useMemo(() => resolveRoute(path, location.search), [path, location.search]);
  const href = (to: string) => (base ? `${base}${to === "/" ? "" : to}` : to);

  const websites = useWebsites();
  const list = useMemo(() => websites.data?.websites ?? [], [websites.data]);
  const loaded = websites.isSuccess;
  const { site: storedRef, setSite } = useScope();

  // What the address says: a site, All sites, or nothing.
  const raw = at?.scope && at.scope !== "all" ? at.scope.raw : null;
  const urlSite: WebsiteRecord | null | undefined = at?.scope === "all" ? null : raw ? findSite(list, raw) ?? undefined : undefined;
  const waiting = !!raw && !urlSite && !loaded;
  const stored = findSite(list, storedRef);
  const site: WebsiteRecord | null = urlSite !== undefined ? urlSite : waiting ? null : stored;

  useEffect(() => {
    if (urlSite !== undefined && (urlSite?.ref ?? null) !== storedRef) setSite(urlSite?.ref ?? null);
  }, [urlSite?.ref, urlSite === null]); // eslint-disable-line react-hooks/exhaustive-deps
  // A remembered site that is no longer one you can open falls back to All sites, never to an empty screen.
  useEffect(() => { if (loaded && storedRef && !stored) setSite(null); }, [loaded, storedRef, !!stored]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (next: WebsiteRecord | null) => {
    setSite(next?.ref ?? null);
    navigate(href(scopeTarget(at, next, ENV, location.search)));
  };

  // The project: what the address says first, then the remembered one.
  const project = useProjectScope();
  const { choice, setChoice, filter } = project;
  const askedProject = parseProjectChoice(new URLSearchParams(location.search).get(PROJECT_PARAM));
  useEffect(() => {
    if (askedProject !== undefined && askedProject !== choice) setChoice(askedProject);
  }, [askedProject]); // eslint-disable-line react-hooks/exhaustive-deps
  // A website the address names outside the project moves the project to All sites; a remembered one just drops.
  useEffect(() => {
    if (choice === null || filter === undefined) return;
    if (urlSite) { const next = choiceForAddress(choice, filter, urlSite); if (next !== choice) setChoice(next); }
    else if (urlSite === undefined && stored && !inProjectFilter(filter, websiteSiteId(stored))) setSite(null);
  }, [urlSite?.ref, stored?.ref, choice, filter]); // eslint-disable-line react-hooks/exhaustive-deps
  /** A project is the first half of the choice: the screen moves to that project's All sites, and the address says which. */
  const pickProject = (next: ProjectChoice) => {
    setChoice(next);
    setSite(null);
    navigate(href(withProjectParam(scopeTarget(at, null, ENV, location.search), next)));
  };
  const projectName = choice === null ? null : choiceLabel(choice, project.projects);

  const [actions, setActions] = useState<HTMLElement | null>(null);
  // On a phone the tabs scroll sideways: bring the current one into view (horizontally only).
  const tabsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const bar = tabsRef.current, on = bar?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!bar || !on || bar.scrollWidth <= bar.clientWidth) return;
    const left = on.offsetLeft - bar.offsetLeft, right = left + on.offsetWidth;
    if (left < bar.scrollLeft || right > bar.scrollLeft + bar.clientWidth) bar.scrollLeft = Math.max(0, left - 24);
  }, [at?.section, at?.tab]);
  // While the website list loads, an address's own site id stands in for the website, so the tabs are already the
  // one-site tabs rather than flashing the All-sites set (a numeric ref is a Xano id; nothing else is assumed).
  const provisional: NavSite | null = waiting && raw ? { ref: raw, name: "", ...(/^\d+$/.test(raw) ? { xano_site_id: Number(raw) } : {}) } : null;
  const tabs = at ? sectionTabs(at.section, site ?? provisional, ENV, location.search) : [];
  const tab = at ? tabs.find((t) => t.id === at.tab) : undefined;
  const views = tab && tab.views.length > 1 ? tab.views : [];
  // A section with one page (Leads) has no tab bar; one that has several keeps its bar even when this scope has
  // only one of them (Review for All sites is Tasks), so the bar always says where you are.
  const showTabs = !!at && sectionOf(at.section).tabs.length > 1 && tabs.length > 0;
  const fullBleed = isFullBleed(path);
  const wide = isWide(path);
  // The site page's overview carries the site's own header, so the chip would only repeat it there (as in the map).
  const showChip = !!at?.scope && !(at.section === "home" && at.tab === "overview" && site);
  const offFor = (hint?: string) => !!hint && !!site && !site.journey_id;
  // Content › Editor: the editor's reads start on hover or focus (prefetch.ts is light: the shell never imports the editor).
  const qc = useQueryClient();
  const warmEditor = () => { if (site?.journey_id) prefetchEditor(qc, site); };

  // The rail's switcher is controlled so the header's site chip can open it too.
  const [scopeOpen, setScopeOpen] = useState(false);
  const switcher = (compact?: boolean) => (
    <ScopeSwitcher site={site} websites={list} pending={websites.isPending} failed={websites.isError} onPick={pick} onPickProject={pickProject} href={href} compact={compact}
      {...(compact ? {} : { open: scopeOpen, onOpenChange: setScopeOpen })} />
  );

  return (
    <NavChromeContext.Provider value={{ v2: true, actions }}>
      <div className="nv" data-testid="nav-v2">
        {/* Phone: brand, switcher, search. */}
        <header className="nv-topbar">
          <Link to={href(sectionHref("home", site, ENV) ?? "/sites")} className="nv-brand" aria-label="Bloxx home"><img src={LOGO} alt="" /><span>Bloxx</span></Link>
          {switcher(true)}
          <button type="button" className="nv-icon-btn" aria-label="Search" onClick={openSearch}><Search /></button>
        </header>

        <nav className="nv-rail" aria-label="Sections" id="main-navigation">
          <Link to={href(sectionHref("home", site, ENV) ?? "/sites")} className="nv-brand"><img src={LOGO} alt="" /><span>Bloxx</span></Link>
          {switcher()}
          <button type="button" className="nv-search" onClick={openSearch} aria-label="Search tables, rows, pages, sites and projects">
            <Search aria-hidden />Search<kbd>{/mac/i.test(typeof navigator !== "undefined" ? navigator.platform : "") ? "⌘K" : "Ctrl K"}</kbd>
          </button>
          {SECTIONS.filter((s) => s.place === "main").map((s) => <RailLink key={s.id} id={s.id} current={at?.section} to={sectionHref(s.id, site, ENV)} href={href} />)}
          <div className="nv-gap" />
          <div className="nv-sep" />
          {SECTIONS.filter((s) => s.place === "foot").map((s) => <RailLink key={s.id} id={s.id} current={at?.section} to={sectionHref(s.id, site, ENV)} href={href} />)}
          <AccountMenu href={href} />
        </nav>

        <main className="nv-panel">
          <div className={cn("nv-scroll app-main", fullBleed && "full")} data-testid="nav-scroll">
            {at && (
              <div className="nv-head" data-testid="section-header">
                <div className={cn("nv-head-in", !showTabs && "solo", wide && "wide")}>
                  <div className="nv-title">
                    <h1>{sectionOf(at.section).label}</h1>
                    {showChip && (waiting
                      ? <span className="nv-chip wait" aria-label="Loading site" />
                      : <button type="button" className="nv-chip" data-testid="scope-chip" aria-label={`${site ? site.name : projectName ? `All sites in ${projectName}` : "All sites"}: change site`} title="Change site" onClick={() => setScopeOpen(true)}><SiteTile site={site} size="sm" />{!site && projectName && <small className="nv-chip-in" data-testid="scope-chip-project">{projectName}</small>}<span>{site ? site.name : "All sites"}</span><ChevronsUpDown className="nv-chip-ud" aria-hidden /></button>)}
                    {readsDataWorkspace(at) && <div className="nv-data-ws" data-testid="data-workspace"><ScopeSelector /></div>}
                    <div className="nv-actions" ref={setActions} />
                  </div>
                  {showTabs && (
                    <div className="nv-tabs" ref={tabsRef} role="tablist" aria-label={sectionOf(at.section).label}>
                      {tabs.map((t) => {
                        const on = t.id === at.tab, off = offFor(t.hint);
                        return (
                          <Link key={t.id} to={href(t.href)} role="tab" aria-selected={on} title={off ? t.hint : undefined} className={cn("nv-tab", off && !on && "off")}
                            {...(t.id === "editor" && site?.journey_id ? { onMouseEnter: warmEditor, onFocus: warmEditor } : {})}>
                            {t.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
            {views.length > 0 && (
              <div className="nv-views">
                <div className="nv-seg" role="group" aria-label={`${tab!.label} views`}>
                  {views.map((v) => <Link key={v.id} to={href(v.href)} aria-current={v.id === at!.view ? "page" : undefined}>{v.label}</Link>)}
                </div>
              </div>
            )}
            {fullBleed ? (
              <div className="min-h-0 flex-1"><RouteContent /></div>
            ) : (
              <div className={cn("container mx-auto px-3", wide ? "max-w-[1680px] md:px-8" : "max-w-6xl md:px-6", views.length ? "pb-6 pt-5 md:pb-8" : "py-6 md:py-8")}><RouteContent /></div>
            )}
          </div>
        </main>

        {/* Phone: the sections. */}
        <nav className="nv-bottombar nv-bb" aria-label="Sections">
          {PHONE.map((id) => {
            const Icon = ICONS[id], to = sectionHref(id, site, ENV);
            return to ? <Link key={id} to={href(to)} aria-current={at?.section === id ? "page" : undefined}><span className="pip"><Icon aria-hidden /></span><span>{sectionOf(id).label}</span></Link> : null;
          })}
          <PhoneMore current={at?.section} site={site} href={href} />
        </nav>

        <CommandPalette />
        <FeedbackRecorder />
      </div>
    </NavChromeContext.Provider>
  );
}

function RailLink({ id, current, to, href }: { id: SectionId; current?: SectionId; to: string | null; href: (to: string) => string }) {
  if (!to) return null;
  const Icon = ICONS[id];
  return (
    <Link to={href(to)} className="nv-sec" aria-current={current === id ? "page" : undefined} data-testid={`rail-${id}`}>
      <Icon aria-hidden />
      <span>{sectionOf(id).label}</span>
    </Link>
  );
}

function useSignOut() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return async () => { await logout(); navigate("/login", { replace: true }); };
}

function ThemeSwitch() {
  const { theme, setTheme } = useTheme();
  const opts: [Theme, string, LucideIcon][] = [["light", "Light", Sun], ["dark", "Dark", Moon], ["system", "Auto", Monitor]];
  return (
    <div className="nv-theme" role="group" aria-label="Theme">
      {opts.map(([t, label, Icon]) => <button key={t} type="button" aria-pressed={theme === t} onClick={() => setTheme(t)}><Icon aria-hidden />{label}</button>)}
    </div>
  );
}

/** The bottom of the rail: who you are, and the things that are about you rather than a site. */
function AccountMenu({ href }: { href: (to: string) => string }) {
  const { user } = useAuth();
  const signOut = useSignOut();
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(false);
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email?.split("@")[0] || "Account";
  const initials = ((user?.first_name?.charAt(0) ?? "") + (user?.last_name?.charAt(0) ?? "")).toUpperCase() || user?.email?.charAt(0).toUpperCase() || null;
  const classic = `${pathname}${search ? `${search}&` : "?"}nav=classic`;
  return (
    <Pop.Root open={open} onOpenChange={setOpen}>
      <Pop.Trigger asChild>
        <button type="button" className="nv-account" aria-label="Account">
          <span className="nv-avatar" aria-hidden>{initials ?? <CircleUser className="h-4 w-4" />}</span>
          <span className="txt"><b>{name}</b>{user?.email && <small>{user.email}</small>}</span>
          <ChevronsUpDown className="nv-updown" aria-hidden />
        </button>
      </Pop.Trigger>
      <Pop.Portal>
        <Pop.Content side="top" align="start" sideOffset={8} collisionPadding={8} className="nv-pop z-50">
          <div className="nv-menu">
            <div className="who"><b>{name}</b>{user?.email && <small>{user.email}</small>}</div>
            <ThemeSwitch />
            <Link className="nv-item" to={href("/billing")} onClick={() => setOpen(false)}><CreditCard aria-hidden />Plan &amp; billing</Link>
            <Link className="nv-item" to={href("/settings/security")} onClick={() => setOpen(false)}><ShieldCheck aria-hidden />Security</Link>
            <Link className="nv-item" to={classic} onClick={() => setOpen(false)}><PanelsTopLeft aria-hidden />Classic navigation</Link>
            <hr />
            <a className="nv-item quiet" href="https://itsbloxx.com/privacy" target="_blank" rel="noreferrer">Privacy policy</a>
            <a className="nv-item quiet" href="https://itsbloxx.com/terms" target="_blank" rel="noreferrer">Terms</a>
            <hr />
            <button type="button" className="nv-item" onClick={() => void signOut()}><LogOut aria-hidden />Log out</button>
          </div>
        </Pop.Content>
      </Pop.Portal>
    </Pop.Root>
  );
}

/** Phone: Settings, Connections, More tools and the account, from the last slot of the bottom bar. */
function PhoneMore({ current, site, href }: { current?: SectionId; site: WebsiteRecord | null; href: (to: string) => string }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const signOut = useSignOut();
  const { pathname, search } = useLocation();
  const here = !!current && !PHONE.includes(current);
  const classic = `${pathname}${search ? `${search}&` : "?"}nav=classic`;
  return (
    <Pop.Root open={open} onOpenChange={setOpen}>
      <Pop.Trigger asChild>
        <button type="button" aria-current={here ? "page" : undefined} aria-label="More sections and account"><span className="pip"><Ellipsis aria-hidden /></span><span>More</span></button>
      </Pop.Trigger>
      <Pop.Portal>
        <Pop.Content side="top" align="end" sideOffset={8} collisionPadding={8} className="nv-pop z-50">
          <div className="nv-menu">
            {user?.email && <div className="who"><b>{[user.first_name, user.last_name].filter(Boolean).join(" ") || "Account"}</b><small>{user.email}</small></div>}
            {(["settings", "connections", "more"] as SectionId[]).map((id) => {
              const Icon = ICONS[id], to = sectionHref(id, site, ENV);
              return to ? <Link key={id} className="nv-item" aria-current={current === id} to={href(to)} onClick={() => setOpen(false)}><Icon aria-hidden />{sectionOf(id).label}</Link> : null;
            })}
            <hr />
            <ThemeSwitch />
            <Link className="nv-item" to={href("/settings/security")} onClick={() => setOpen(false)}><ShieldCheck aria-hidden />Security</Link>
            <Link className="nv-item" to={classic} onClick={() => setOpen(false)}><PanelsTopLeft aria-hidden />Classic navigation</Link>
            <button type="button" className="nv-item" onClick={() => void signOut()}><LogOut aria-hidden />Log out</button>
          </div>
        </Pop.Content>
      </Pop.Portal>
    </Pop.Root>
  );
}
